import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  archiveSupplierAgreementDocument,
  attachSupplierAgreementDocument,
  listSupplierAgreementDocuments,
  listSupplierAgreementPrices,
  setSupplierAgreementPrice,
} from "../../services/supplier-agreement.service";
import type {
  AttachSupplierAgreementDocumentInput,
  SetSupplierAgreementPriceInput,
} from "../../types/supplier-agreement.type";

export function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return fallback;
}

export function useSupplierAgreementPrices(organizationId: string | undefined, supplierId: string | undefined) {
  return useQuery({
    queryKey: ["supplier-agreement-prices", organizationId, supplierId],
    queryFn: () => listSupplierAgreementPrices(organizationId as string, supplierId as string),
    enabled: !!organizationId && !!supplierId,
  });
}

export function useSetSupplierAgreementPrice(organizationId: string | undefined, supplierId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetSupplierAgreementPriceInput) =>
      setSupplierAgreementPrice(organizationId as string, supplierId as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["supplier-agreement-prices", organizationId, supplierId] });
    },
  });
}

export function useSupplierAgreementDocuments(organizationId: string | undefined, supplierId: string | undefined) {
  return useQuery({
    queryKey: ["supplier-agreement-documents", organizationId, supplierId],
    queryFn: () => listSupplierAgreementDocuments(organizationId as string, supplierId as string),
    enabled: !!organizationId && !!supplierId,
  });
}

export function useAttachSupplierAgreementDocument(organizationId: string | undefined, supplierId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AttachSupplierAgreementDocumentInput) =>
      attachSupplierAgreementDocument(organizationId as string, supplierId as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["supplier-agreement-documents", organizationId, supplierId] });
    },
  });
}

export function useArchiveSupplierAgreementDocument(organizationId: string | undefined, supplierId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => archiveSupplierAgreementDocument(organizationId as string, supplierId as string, documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["supplier-agreement-documents", organizationId, supplierId] });
    },
  });
}
